/**
 * Display helpers for lead status and property type.
 * IDs and option lists come from APIs — do not hardcode them here.
 */

import { isValidGuid } from '../../../shared/utils/guidValidation';

export function idsEqual(a, b) {
  return String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase();
}

/** Compact status name for comparisons: "Not Interested" and "NotInterested" match. */
export function statusNameKey(name) {
  return String(name ?? '').replace(/\s+/g, '').toLowerCase();
}

/** Human-readable labels for leftover CamelCase names from older rows. */
const STATUS_DISPLAY_NAMES = {
  AssignedToEmployee: 'Assigned To Employee',
  HotClient: 'Hot Client',
  Interested: 'Interested',
  NotInterested: 'Not Interested',
  PhoneNotPicked: 'Phone Not Picked',
  InvalidNumber: 'Invalid Number',
  CallBackLater: 'Call Back Later',
  UpcomingVisit: 'Upcoming Visit',
  Complete: 'Complete',
  Visited: 'Visited',
  DealDone: 'Deal Done',
  PaymentPending: 'Payment Pending',
  Closed: 'Closed',
  New: 'New',
  NewImported: 'New',
  ContactAfterSomeTime: 'Contact After Some Time',
};

export const getStatusDisplayName = (name) => {
  if (name == null || name === '') return 'Unknown';
  if (STATUS_DISPLAY_NAMES[name]) return STATUS_DISPLAY_NAMES[name];
  return String(name)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
};

export function lookupByStatusName(map, name) {
  if (!name || !map) return undefined;
  if (map[name]) return map[name];
  const compact = String(name).replace(/\s+/g, '');
  if (map[compact]) return map[compact];
  const key = statusNameKey(name);
  const hit = Object.keys(map).find((k) => statusNameKey(k) === key);
  return hit ? map[hit] : undefined;
}

export function findById(list, id) {
  if (!Array.isArray(list) || id == null || id === '') return null;
  return list.find((item) => idsEqual(item.id ?? item.Id, id)) ?? null;
}

export function findStageIdByName(stages, ...names) {
  const list = Array.isArray(stages) ? stages : [];
  for (const name of names) {
    const hit = list.find(
      (stage) => statusNameKey(stage?.name ?? stage?.leadStatusName) === statusNameKey(name)
    );
    const id = hit?.id ?? hit?.Id;
    if (isValidGuid(id)) return String(id);
  }
  return '';
}

export function buildAssignedLeadsPath({
  leadStatusId,
  fromDate,
  toDate,
  assigneeUserId,
} = {}) {
  const params = new URLSearchParams();
  if (isValidGuid(leadStatusId)) params.set('leadStatusId', String(leadStatusId).trim());
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  if (isValidGuid(assigneeUserId)) params.set('assigneeUserId', String(assigneeUserId).trim());
  const qs = params.toString();
  return qs ? `/leadassigned?${qs}` : '/leadassigned';
}

export function matchesLeadStatusFilter(row, statusParam, stages = []) {
  if (statusParam == null || statusParam === '') return true;
  const param = String(statusParam);
  if (idsEqual(row?.leadStatusId ?? row?.LeadStatusId, param)) return true;

  const rowName =
    row?.leadStatus?.leadStatusName
    ?? row?.LeadStatus?.leadStatusName
    ?? findById(stages, row?.leadStatusId ?? row?.LeadStatusId)?.leadStatusName
    ?? findById(stages, row?.leadStatusId ?? row?.LeadStatusId)?.name
    ?? '';
  if (rowName && statusNameKey(rowName) === statusNameKey(param)) return true;
  if (statusNameKey(param) === 'callbacklater' && statusNameKey(rowName) === 'contactaftersometime') return true;

  const paramStage = (Array.isArray(stages) ? stages : []).find((stage) =>
    idsEqual(stage.id ?? stage.Id, param)
    || statusNameKey(stage.name ?? stage.leadStatusName) === statusNameKey(param)
  );
  if (paramStage) {
    return idsEqual(row?.leadStatusId ?? row?.LeadStatusId, paramStage.id ?? paramStage.Id);
  }
  return false;
}

export function leadAssignedMatchesSearch(row, term) {
  const q = String(term ?? '').trim().toLowerCase();
  if (!q) return true;
  const lead = row?.lead ?? row?.Lead;
  const haystacks = [
    row?.conversationComment,
    lead?.firstName,
    lead?.lastName,
    lead?.contactNumber,
    lead?.email,
    lead?.leadCode,
  ];
  return haystacks.some((value) => String(value ?? '').toLowerCase().includes(q));
}

export function matchesAssignedEmployee(row, employeeId, loggedInUserId) {
  if (employeeId == null || employeeId === '') return true;
  return idsEqual(loggedInUserId, employeeId)
    || idsEqual(row?.userId ?? row?.UserId, employeeId);
}

export function resolvePropertyType(value, types = []) {
  if (value == null || value === '') return null;
  const rows = Array.isArray(types) ? types : [];
  return (
    rows.find(
      (p) => idsEqual(p.id, value) || p.name === value || p.propertyTypeName === value
    ) || null
  );
}

export const STATUS_STYLE_MAP = {
  AssignedToEmployee: { bg: '#f4f6fb', color: '#8392ab', dot: '#8392ab' },
  HotClient: { bg: '#fff0f0', color: '#f5365c', dot: '#f5365c' },
  Interested: { bg: '#f0fff8', color: '#2dce89', dot: '#2dce89' },
  NotInterested: { bg: '#f4f6fb', color: '#8392ab', dot: '#8392ab' },
  PhoneNotPicked: { bg: '#fffbf0', color: '#faad14', dot: '#faad14' },
  InvalidNumber: { bg: '#fff0f0', color: '#f5365c', dot: '#f5365c' },
  ContactAfterSomeTime: { bg: '#f9f0ff', color: '#722ed1', dot: '#722ed1' },
  CallBackLater: { bg: '#f9f0ff', color: '#722ed1', dot: '#722ed1' },
  UpcomingVisit: { bg: '#fff7f0', color: '#fb6340', dot: '#fb6340' },
  Complete: { bg: '#f0fff8', color: '#2dce89', dot: '#2dce89' },
  Visited: { bg: '#f0faff', color: '#11cdef', dot: '#11cdef' },
  DealDone: { bg: '#f0fff4', color: '#00875a', dot: '#00875a' },
  PaymentPending: { bg: '#fffde7', color: '#e65100', dot: '#e65100' },
  Closed: { bg: '#f3f4f6', color: '#374151', dot: '#374151' },
  New: { bg: '#f1f5f9', color: '#64748b', dot: '#64748b' },
  NewImported: { bg: '#f1f5f9', color: '#64748b', dot: '#64748b' },
};

const DEFAULT_STYLE = { bg: '#f4f6fb', color: '#8392ab', dot: '#8392ab' };

function styleFromColor(color) {
  if (!color) return null;
  return { bg: `${color}18`, color, dot: color };
}

export const getStatusName = (id, statuses = []) =>
  findById(statuses, id)?.name
    ?? findById(statuses, id)?.leadStatusName
    ?? 'Unknown';

export const getStatusColor = (nameOrId, statuses = []) => {
  const fromList = findById(statuses, nameOrId);
  if (fromList?.color) return fromList.color;
  const byName = statuses.find((s) => statusNameKey(s.name ?? s.leadStatusName) === statusNameKey(nameOrId));
  if (byName?.color) return byName.color;
  return lookupByStatusName(STATUS_STYLE_MAP, nameOrId)?.color ?? '#8392ab';
};

export const getStatusStyle = (name, color) =>
  styleFromColor(color) ?? lookupByStatusName(STATUS_STYLE_MAP, name) ?? DEFAULT_STYLE;

export const getPropertyTypeName = (id, types = []) =>
  resolvePropertyType(id, types)?.name ?? 'Unknown';

export function getTableStatusClass(name) {
  switch (statusNameKey(name)) {
    case 'interested':
      return 'table-success';
    case 'hotclient':
      return 'table-danger';
    case 'notinterested':
    case 'visited':
      return 'table-info';
    case 'callbacklater':
    case 'contactaftersometime':
    case 'upcomingvisit':
      return 'table-warning';
    case 'invalidnumber':
    case 'complete':
      return 'table-primary';
    default:
      return '';
  }
}
